import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './auth';
import { LoginPage } from './components/Auth';
import { APIDataLoader } from './components/APIDataLoader';
import MFASetup from './components/Auth/MFASetup';
import { MFAVerify } from './components/Auth/MFAVerify';
import { MFAGraceBanner } from './components/Auth/MFAGraceBanner';
import App from './App';
import { APIStorage } from './services/apiStorage';

export default function AppRouter() {
  const { isAuthenticated, isLoading, user, mfaState, updateMfaState } = useAuth();
  const [isInitializing, setIsInitializing] = useState(true);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const initialize = async () => {
      if (hasInitialized.current) {
        setIsInitializing(false);
        return;
      }

      if (!isAuthenticated || !user) {
        setIsInitializing(false);
        return;
      }

      hasInitialized.current = true;
      console.log('AppRouter: Benutzer authentifiziert:', user.name, user.id);
      setIsInitializing(false);
    };

    if (!isLoading) {
      void initialize();
    }
  }, [isAuthenticated, isLoading, user]);

  const handleMfaVerified = useCallback((token: string) => {
    // Token wird bereits im apiStorage gesetzt
    void token;
    updateMfaState({ mfaVerified: true });
  }, [updateMfaState]);

  const handleMfaSetupComplete = useCallback((token: string) => {
    void token;
    updateMfaState({ mfaEnabled: true, mfaVerified: true });
    setShowMfaSetup(false);
  }, [updateMfaState]);

  const handleSetupClick = useCallback(() => {
    setShowMfaSetup(true);
  }, []);

  // Ladebildschirm während Auth-Prüfung
  if (isLoading || isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-gray-900 dark:border-gray-100 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-600">Wird geladen...</p>
        </div>
      </div>
    );
  }

  // Nicht angemeldet -> Login-Seite
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // MFA aktiviert aber noch nicht verifiziert → MFA-Verify-Seite
  // Überspringen wenn Entra-MFA aktiv (User hat bereits bei Microsoft MFA durchlaufen)
  if (mfaState.mfaEnabled && !mfaState.mfaVerified && !mfaState.entraMfaVerified) {
    return (
      <MFAVerify
        onVerified={handleMfaVerified}
        onCancel={() => {
          // Logout bei Abbruch
          const storage = new APIStorage();
          storage.clearToken();
          window.location.reload();
        }}
      />
    );
  }

  // Grace Period abgelaufen und MFA nicht eingerichtet → Setup erzwingen
  // Nur wenn Entra-MFA NICHT aktiv ist
  if (!mfaState.mfaEnabled && mfaState.graceExpired && !mfaState.entraMfaVerified) {
    return (
      <MFASetup
        userEmail={user?.email || ''}
        onComplete={handleMfaSetupComplete}
      />
    );
  }

  // MFA Setup Wizard angefordert (z.B. über Grace-Banner)
  if (showMfaSetup) {
    return (
      <MFASetup
        userEmail={user?.email || ''}
        onComplete={handleMfaSetupComplete}
      />
    );
  }

  // Hauptanwendung mit API-Daten-Synchronisation
  return (
    <APIDataLoader>
      <MfaGraceBannerWrapper
        mfaState={mfaState}
        onSetupClick={handleSetupClick}
      />
      <App />
    </APIDataLoader>
  );
}

/**
 * Wrapper-Komponente für den Grace-Period-Banner.
 * Wird nur gerendert, wenn MFA nicht aktiviert und Grace Period aktiv ist.
 */
function MfaGraceBannerWrapper({
  mfaState,
  onSetupClick,
}: {
  mfaState: { mfaEnabled: boolean; entraMfaVerified: boolean; graceDaysRemaining: number; graceExpired: boolean };
  onSetupClick: () => void;
}) {
  // Kein Banner wenn Entra-MFA aktiv oder App-MFA bereits eingerichtet
  if (mfaState.entraMfaVerified || mfaState.mfaEnabled || (mfaState.graceDaysRemaining <= 0 && !mfaState.graceExpired)) {
    return null;
  }

  return (
    <MFAGraceBanner
      graceDaysRemaining={mfaState.graceDaysRemaining}
      graceExpired={mfaState.graceExpired}
      onSetupClick={onSetupClick}
    />
  );
}
